// src/components/SalaryDashboard.jsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  ThemeProvider,
  createTheme,
  CircularProgress,
  Typography,
  Button,
  Stack,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Controls from "./Controls";
import KPICards from "./KPICards";
import SalaryTable from "./Table";
import { useLazyGetAgencySalaryDetailsQuery } from "../../../redux/api/salary";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import dayjs from "dayjs";
import BackButton from "../../../components/BackButton/BackButton";

const PKR = (n) =>
  `Rs ${n.toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

export default function SalarySheet() {
  const [date, setDate] = useState(dayjs());
  const [q, setQ] = useState("");
  const currentAgency = useSelector(selectCurrentagency);
  const navigate = useNavigate();
  const outerTheme = useTheme();

  // Extend the app theme instead of replacing it, so dark mode (and the rest
  // of the palette) carries over into this page.
  const theme = useMemo(
    () =>
      createTheme(outerTheme, {
        palette: {
          primary: { main: "#10b981" },
          success: { main: "#16a34a" },
          warning: { main: "#f59e0b" },
        },
        components: {
          MuiPaper: { styleOverrides: { root: { borderRadius: 16 } } },
        },
      }),
    [outerTheme]
  );

  const [getAgencySalaryDetails, { data: apiData, isLoading, isError }] =
    useLazyGetAgencySalaryDetailsQuery();

  const agencyId = currentAgency?.agencyid;

  const handleGetRecords = () => {
    if (!agencyId || !date) return;
    getAgencySalaryDetails({
      agencyId,
      month: date.month() + 1,
      year: date.year(),
    });
  };

  useEffect(() => {
    if (agencyId && date) {
      handleGetRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId]);

  // Transform API → table model
  const salaryData = useMemo(() => {
    return (apiData || []).map((e) => {
      const allowances = (e.Commission || 0) + (e.Bonus || 0) + (e?.overtime || 0)  + (e?.allowances || 0);
      const deductions = e.Deductions || 0;
      const net =
        e.NetSalary ??
        (e.BaseSalary || 0) + allowances - deductions - (e.advancesalary || 0);
      return {
        id: String(e.agentid),
        name: `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim(),
        role:e.roleName,
        basic: e.BaseSalary || 0,
        allowances,
        deductions,
        advance: e.advancesalary || 0,
        net,
        status: net > 0 ? "Paid" : "Unpaid",
      };
    });
  }, [apiData]);

  // Frontend filter (q)
  const filtered = useMemo(() => {
    if (!q.trim()) return salaryData;
    const s = q.toLowerCase();
    return salaryData.filter(
      (e) =>
        e.id.toLowerCase().includes(s) ||
        e.name.toLowerCase().includes(s) ||
        e.role.toLowerCase().includes(s)
    );
  }, [q, salaryData]);

  // Totals (all rows)
  const totals = useMemo(() => {
    if (!salaryData.length)
      return {
        staff: 0,
        paid: 0,
        unpaid: 0,
        sumBasic: 0,
        sumAllow: 0,
        sumDed: 0,
        sumAdv: 0,
        sumNet: 0,
      };
    return {
      staff: salaryData.length,
      paid: salaryData.filter((e) => e.status === "Paid").length,
      unpaid: salaryData.filter((e) => e.status === "Unpaid").length,
      sumBasic: salaryData.reduce((a, b) => a + b.basic, 0),
      sumAllow: salaryData.reduce((a, b) => a + b.allowances, 0),
      sumDed: salaryData.reduce((a, b) => a + b.deductions, 0),
      sumAdv: salaryData.reduce((a, b) => a + b.advance, 0),
      sumNet: salaryData.reduce((a, b) => a + b.net, 0),
    };
  }, [salaryData]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          p: { xs: 2, md: 4 },
          borderRadius: "10px",
          background: (t) =>
            t.palette.mode === "dark"
              ? "linear-gradient(180deg,#16241c 0%,#12161f 60%)"
              : "linear-gradient(180deg,#F6FFFB 0%,#FFFFFF 60%)",
        }}
      >
        {/* 🔙 Back Button */}
              <BackButton sx={{ mb: 2 }} label="Go Back" />


        {/* Header */}
        <Header
          month={date.month()}
          year={date.year()}
          exportRows={apiData} // export exactly what user sees
          totals={totals}
        />

        <Controls
          date={date}
          setDate={setDate}
          q={q}
          setQ={setQ}
          onGetRecords={handleGetRecords}
        />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress color="success" />
          </Box>
        ) : isError ? (
          <Typography color="error" align="center" sx={{ mt: 4 }}>
            Failed to fetch salary details ❌
          </Typography>
        ) : (
          <>
            <KPICards totals={totals} />
            <SalaryTable data={filtered} totals={totals} PKR={PKR} />
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}
